import { LoadingView, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { useNicheLabels } from '../../components/dashboard/useNicheLabels';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { Label } from '@app/engine';
import { CommonConstants, FormatLocalized } from '@shared/common';
import { BillingClient, BookingClient, CallClient, KBClient, LeadClient, TenantClient, TextsClient } from '@shared/frontend';
import { Link } from 'react-router-dom';

/** File-local constants. */
const localConstants = {
  placeholderName: 'there',
  // Recency window for the "recent" bubbles: both count activity within this trailing number of days.
  recentWindowDays: 7
} as const;

/**
 * Overview dashboard screen: high-level counts and service status.
 *
 * @returns The overview element.
 */
export function Overview() {
  const { tenantID, tenantUser, tenantLoading } = useDashboard();
  const labels = useNicheLabels();

  // Rolling cutoff for the recent bubbles, passed to the count endpoints as an ISO lower bound so the counts stay correct beyond the first list page.
  const recentSince = new Date(
    Date.now() - localConstants.recentWindowDays * CommonConstants.HrPerDay * CommonConstants.MinPerHour * CommonConstants.SecPerMinute * CommonConstants.MsPerSecond
  ).toISOString();
  const calls = useDashboardData((signal) => CallClient.CountCalls(signal, tenantID!, undefined, recentSince), [tenantID]);
  const texts = useDashboardData((signal) => TextsClient.CountTexts(signal, tenantID!, undefined, recentSince), [tenantID]);
  const leads = useDashboardData((signal) => LeadClient.ListLeads(signal, tenantID!), [tenantID]);
  const bookings = useDashboardData((signal) => BookingClient.ListBookings(signal, tenantID!), [tenantID]);
  const access = useDashboardData((signal) => BillingClient.GetAccess(signal, tenantID!), [tenantID]);
  // The AI Number is resolved from the authenticated owner server-side, so this read needs no tenantID argument.
  const number = useDashboardData((signal) => TenantClient.GetMyNumber(signal), []);
  // Next-step alert sources: the business profile (call-handling instructions live on it) and the tenant's knowledge documents.
  const profile = useDashboardData((signal) => TenantClient.GetProfile(signal, tenantID!), [tenantID]);
  const knowledge = useDashboardData((signal) => KBClient.ListDocuments(signal, tenantID!), [tenantID]);

  if (tenantLoading && !tenantUser) return <LoadingView />;

  const openLeads = (leads.data ?? []).filter((lead) => !lead.contacted).length;
  // Bookings have no manual resolve like leads, so they clear by schedule: only appointments whose end time has not passed count toward the card.
  const upcomingBookings = (bookings.data ?? []).filter((booking) => new Date(booking.endISO).getTime() >= Date.now()).length;
  const aiOn = access.data?.allowAI ?? false;
  const aiNumber = number.data?.e164 ?? null;
  // Service-status hint: while answering is on and the AI Number is provisioned, lead with the number itself (the line the tenant's clients call); otherwise fall back to the plain on/off state.
  // Call-forwarding is a subscription-derived backend gate with no owner-facing toggle and is not a forward to the contact phone, so it is intentionally omitted here.
  const statusHint = aiOn && aiNumber ? FormatLocalized(Localized.OverviewAINumber, aiNumber) : aiOn ? Localized.OverviewAIOn : Localized.OverviewAIOff;
  // Payment-overdue alert: surfaced only for the dunning states (past_due keeps the AI alive during the grace window, suspended has already paused it) so the owner is prompted to fix billing before
  // the subscription is cancelled. A clean active/trialing subscription, a deliberate cancellation, and the no-subscription case never trip it (the latter two are not "overdue").
  const billingStatus = CommonConstants.Database.BillingStatus;
  const paymentOverdue = access.data?.status === billingStatus.PastDue || access.data?.status === billingStatus.Suspended;
  // Next-step alerts (danger accent): walk a fresh tenant through the two setup gaps the AI needs most — call-handling instructions first, then business knowledge. One at a time: the
  // knowledge alert appears only once instructions exist. Both wait for their loads so neither flashes before its data lands.
  const instructionsMissing = !profile.loading && (profile.data?.agentInstructions ?? '').trim().length === 0;
  const knowledgeMissing = !knowledge.loading && (knowledge.data ?? []).length === 0;
  const showKnowledgeStep = !profile.loading && !instructionsMissing && knowledgeMissing;
  const displayName = tenantUser?.displayName ?? localConstants.placeholderName;

  return (
    <div>
      <Label classNameContainer={Styles.muted} text={Localized.OverviewWelcome.replace('{0}', displayName)} />

      <div className={Styles.cardGrid}>
        {paymentOverdue ? (
          <Link to={Constants.Route.Billing} title={Localized.OverviewPaymentOverdueHint} className={`${Styles.card} ${Styles.cardClickable} ${Styles.cardAlert}`}>
            <Label classNameContainer={Styles.statLabel} text={Localized.OverviewPaymentOverdue} title="" />
            <Label classNameContainer={Styles.statValue} text="!" title="" />
            <Label classNameContainer={Styles.statHint} text={Localized.OverviewPaymentOverdueHint} title="" />
          </Link>
        ) : null}
        <Link to={Constants.Route.Calls} title={Localized.OverviewCallsHint} className={`${Styles.card} ${Styles.cardClickable}`}>
          <Label classNameContainer={Styles.statLabel} text={Localized.OverviewCalls} title="" />
          <Label classNameContainer={Styles.statValue} text={calls.loading ? '—' : String(calls.data ?? 0)} title="" />
        </Link>
        <Link to={Constants.Route.Texts} title={Localized.OverviewTextsHint} className={`${Styles.card} ${Styles.cardClickable}`}>
          <Label classNameContainer={Styles.statLabel} text={Localized.OverviewTexts} title="" />
          <Label classNameContainer={Styles.statValue} text={texts.loading ? '—' : String(texts.data ?? 0)} title="" />
        </Link>
        <Link to={Constants.Route.Leads} title={Localized.OverviewLeadsHint} className={`${Styles.card} ${Styles.cardClickable}`}>
          <Label classNameContainer={Styles.statLabel} text={`${Localized.SharedOpen} ${labels.leads}`} title="" />
          <Label classNameContainer={Styles.statValue} text={leads.loading ? '—' : String(openLeads)} title="" />
        </Link>
        <Link to={Constants.Route.Bookings} title={Localized.OverviewBookingsHint} className={`${Styles.card} ${Styles.cardClickable}`}>
          <Label classNameContainer={Styles.statLabel} text={labels.bookings} title="" />
          <Label classNameContainer={Styles.statValue} text={bookings.loading ? '—' : String(upcomingBookings)} title="" />
        </Link>
        <Link to={Constants.Route.Billing} title={Localized.OverviewServiceStatusHint} className={`${Styles.card} ${Styles.cardClickable}`}>
          <Label classNameContainer={Styles.statLabel} text={Localized.OverviewServiceStatus} title="" />
          <Label classNameContainer={Styles.statValue} text={aiOn ? Localized.SharedActive : Localized.SharedInactive} title="" />
          <Label classNameContainer={Styles.statHint} text={statusHint} title="" />
        </Link>
        {/* Next-step alerts trail the stat cards so they sit on the right of the grid. Their inline hint doubles as the container tooltip. */}
        {instructionsMissing ? (
          <Link to={Constants.Route.Profile} title={Localized.OverviewAddCallInstructionsHint} className={`${Styles.card} ${Styles.cardClickable} ${Styles.cardAlert}`}>
            <Label classNameContainer={Styles.statLabel} text={Localized.OverviewNextStep} title="" />
            <Label classNameContainer={Styles.statValue} text={Localized.OverviewAddCallInstructions} title="" />
            <Label classNameContainer={Styles.statHint} text={Localized.OverviewAddCallInstructionsHint} title="" />
          </Link>
        ) : null}
        {showKnowledgeStep ? (
          <Link to={Constants.Route.Knowledge} title={Localized.OverviewAddKnowledgeHint} className={`${Styles.card} ${Styles.cardClickable} ${Styles.cardAlert}`}>
            <Label classNameContainer={Styles.statLabel} text={Localized.OverviewNextStep} title="" />
            <Label classNameContainer={Styles.statValue} text={Localized.OverviewAddKnowledge} title="" />
            <Label classNameContainer={Styles.statHint} text={Localized.OverviewAddKnowledgeHint} title="" />
          </Link>
        ) : null}
      </div>
    </div>
  );
}
